import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scale } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmailPasswordTab } from './login/EmailPasswordTab';
import { MagicLinkTab } from './login/MagicLinkTab';

/**
 * Login page — Supabase auth via email+password OR magic link.
 *
 * WCAG 2.1 AA compliance:
 * - Visible <label> for every input, linked via htmlFor/id.
 * - aria-describedby links each input to its error message element.
 * - aria-invalid="true" set on inputs that have validation errors.
 * - Error messages rendered with role="alert" for immediate SR announcement.
 * - Focus ring on all interactive elements via focus-visible classes.
 * - Loading state conveyed via aria-busy + spinner + button text change.
 * - Skip link present as first focusable element (WCAG 2.4.1).
 * - Tab list follows ARIA APG tablist pattern (see components/ui/tabs).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { profile, isLoading: authLoading } = useAuth();

  // Redirect already-authenticated users immediately (incl. after magic
  // link callback: Supabase JS auto-detects #access_token in the URL hash,
  // fires onAuthStateChange, AuthProvider sets profile, this fires).
  useEffect(() => {
    if (!authLoading && profile) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, profile, navigate]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-background focus:px-4 focus:py-2 focus:shadow-md focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Ambient gradient — same family as WelcomePage so the transition feels
          continuous when the CTA button is clicked. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-background via-background to-primary/5"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-32 -z-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl"
      />

      <main id="main-content" role="main" className="w-full max-w-sm space-y-6 px-4">
        <div className="flex flex-col items-center gap-2">
          <Link
            to="/"
            aria-label="CMP home"
            className="rounded-full p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Scale className="h-10 w-10 text-primary" aria-hidden="true" />
          </Link>
          <h1 className="text-2xl font-bold text-foreground">CMP</h1>
          <p className="text-sm text-muted-foreground">Contract Management Platform</p>
        </div>

        <div className="space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-foreground">Sign in</h2>

          <Tabs defaultValue="password" className="space-y-4">
            <TabsList className="w-full">
              <TabsTrigger value="password" className="flex-1">
                Email &amp; password
              </TabsTrigger>
              <TabsTrigger value="magic" className="flex-1">
                Magic link
              </TabsTrigger>
            </TabsList>

            <TabsContent value="password" aria-label="Email and password sign in">
              <EmailPasswordTab />
            </TabsContent>
            <TabsContent value="magic" aria-label="Magic link sign in">
              <MagicLinkTab />
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          New to CMP? Accounts are created by your administrator.
        </p>
      </main>
    </div>
  );
}
